import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const ModalTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.4rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--clr-sec);
  width: 30px;
  height: 30px;
  border-radius: 50%;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const UserInfo = styled.div`
  background-color: var(--clr-bg);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  border-left: 4px solid #ff9500;
`;

const UserIdText = styled.p`
  margin: 0;
  color: var(--clr-sec);
  font-family: monospace;
  font-size: 0.85rem;
  opacity: 0.8;
  word-break: break-all;
`;

const MessageText = styled.p`
  margin: 0.5rem 0 0 0;
  color: var(--clr-sec);
  font-style: italic;
  word-break: break-word;
`;

const HistoryBox = styled.div`
  background-color: var(--clr-bg);
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  color: var(--clr-sec);
  font-size: 0.9rem;
`;

const HistoryCount = styled.div`
  font-weight: 600;
  color: ${(props) => (props.count > 0 ? '#ff9500' : 'var(--clr-sec)')};
  margin-bottom: ${(props) => (props.count > 0 ? '0.5rem' : '0')};
`;

const HistoryList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  max-height: 120px;
  overflow-y: auto;
`;

const HistoryItem = styled.li`
  margin-bottom: 0.35rem;
  opacity: 0.85;
  font-size: 0.85rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--clr-sec);
  font-weight: 500;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  resize: vertical;
  font-family: inherit;
  font-size: 0.95rem;
  box-sizing: border-box;
  background-color: var(--clr-bg);
  color: var(--clr-sec);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.7rem 1.25rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  color: var(--clr-sec);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const WarnButton = styled(Button)`
  background-color: #ff9500;
  color: #fff;

  &:hover:not(:disabled) {
    background-color: #e68600;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  background-color: rgba(255, 68, 68, 0.1);
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #2ecc71;
  background-color: rgba(46, 204, 113, 0.1);
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const ModWarnModal = ({ isOpen, onClose, message, room = 'general' }) => {
  const [reason, setReason] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Load warning history for the target user when modal opens
  useEffect(() => {
    if (!isOpen || !message?.userId) {
      setHistory(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) return;
        const res = await fetch(
          `${API_URL}mod/warnings/${encodeURIComponent(message.userId)}?room=${encodeURIComponent(room)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setHistory(data);
        }
      } catch (err) {
        console.error('Failed to load warning history:', err);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, message?.userId, room]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason.trim()) {
      setError('Please provide a reason for the warning (shown to user)');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuth().currentUser.getIdToken();

      const response = await fetch(`${API_URL}mod/warn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: message.userId,
          reason: reason.trim(),
          internalNote: internalNote.trim() || undefined,
          room,
          messageText: message.messageText || null,
          messageId: message.id || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Warning sent (user now has ${data.warningCount} warning${data.warningCount === 1 ? '' : 's'})`
        );
        setReason('');
        setInternalNote('');
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 1500);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to send warning');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setInternalNote('');
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen || !message) return null;

  const count = history?.warningCount ?? 0;
  const previousWarnings = history?.warnings || [];

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>⚠️ Warn user</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <UserInfo>
          <UserIdText>User ID: {message.userId}</UserIdText>
          {message.messageText && (
            <MessageText>"{message.messageText}"</MessageText>
          )}
        </UserInfo>

        <HistoryBox>
          {historyLoading ? (
            <div>Loading warning history...</div>
          ) : (
            <>
              <HistoryCount count={count}>
                Prior warnings: {count}
              </HistoryCount>
              {previousWarnings.length > 0 && (
                <HistoryList>
                  {previousWarnings.slice(0, 5).map((w) => (
                    <HistoryItem key={w.id}>
                      {formatDate(w.createdAt)} — {w.reason}
                      {w.modNickname ? ` (by ${w.modNickname})` : ''}
                    </HistoryItem>
                  ))}
                </HistoryList>
              )}
            </>
          )}
        </HistoryBox>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Reason (shown to user in pop-up)</Label>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Please keep the chat respectful — no personal attacks."
              maxLength={1000}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>Internal note (mods only, optional)</Label>
            <TextArea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Private context for other mods reviewing this user later"
              maxLength={1000}
              disabled={loading}
              style={{ minHeight: '60px' }}
            />
          </FormGroup>

          <ButtonRow>
            <CancelButton type="button" onClick={handleClose} disabled={loading}>
              Cancel
            </CancelButton>
            <WarnButton type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send warning'}
            </WarnButton>
          </ButtonRow>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ModWarnModal;
